import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from "recharts";
import {
  Radar,
  ShieldCheck,
  TriangleAlert,
  CalendarClock,
  ArrowRight,
  Activity,
} from "lucide-react";
import { TenantLayout, useTenant } from "@/components/tenant/tenant-layout";
import { listObligations } from "@/lib/api/obligations";
import { listAuditEvents } from "@/lib/api/audit";
import { getLatestMfnEvaluation } from "@/lib/api/mfn";
import { computePortfolioRadar, type RadarTrigger } from "@/lib/mfn/portfolio";
import { Card, CardHeader, Badge, EmptyState, Spinner, Dot } from "@/components/ui/primitives";
import { CATEGORY_LABELS } from "@argus/engine";
import { fmtUsd, fmtDate, fmtDateTime, relativeDays, relativeTime } from "@/lib/format";

function PostureRing({ score }: { score: number }) {
  const r = 52;
  const c = 2 * Math.PI * r;
  const pct = score / 100;
  const tone = score >= 85 ? "#34d399" : score >= 60 ? "#fbbf24" : "#f87171";
  return (
    <div className="relative flex size-32 items-center justify-center">
      <svg className="size-32 -rotate-90" viewBox="0 0 120 120">
        <circle
          cx="60"
          cy="60"
          r={r}
          fill="none"
          stroke="currentColor"
          className="text-neutral-800"
          strokeWidth="9"
        />
        <circle
          cx="60"
          cy="60"
          r={r}
          fill="none"
          stroke={tone}
          strokeWidth="9"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - pct)}
          style={{ transition: "stroke-dashoffset 900ms ease" }}
        />
      </svg>
      <div className="absolute flex flex-col items-center">
        <span className="text-3xl font-semibold tabular-nums tracking-tight text-neutral-50">
          {score}
        </span>
        <span className="text-[10px] uppercase tracking-wider text-neutral-500">posture</span>
      </div>
    </div>
  );
}

function SeverityBadge({ t }: { t: { severity: RadarTrigger["severity"] } }) {
  if (t.severity === "breach")
    return (
      <Badge tone="breach">
        <Dot tone="breach" /> Active breach
      </Badge>
    );
  if (t.severity === "elevated")
    return (
      <Badge tone="risk">
        <Dot tone="risk" /> Elevated
      </Badge>
    );
  return (
    <Badge tone="info">
      <Dot tone="info" /> Review
    </Badge>
  );
}

function DashboardInner() {
  const tenant = useTenant();

  const { data: radar, isLoading: radarLoading } = useQuery({
    queryKey: ["portfolio-radar", tenant.id],
    queryFn: () => computePortfolioRadar(tenant.id),
  });
  const { data: obligations } = useQuery({
    queryKey: ["obligations", tenant.id, false],
    queryFn: () => listObligations(tenant.id, { activeOnly: true }),
  });
  const { data: recentAudit } = useQuery({
    queryKey: ["audit", tenant.id, "", "recent"],
    queryFn: () => listAuditEvents(tenant.id, { limit: 7 }),
  });
  // Fail-soft: null both pre-migration and before any evaluation has ever
  // run — the badge simply doesn't render either way.
  const { data: latestEvaluation } = useQuery({
    queryKey: ["mfn-latest-evaluation", tenant.id],
    queryFn: () => getLatestMfnEvaluation(tenant.id),
  });

  const today = new Date().toISOString().slice(0, 10);
  const soonIso = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);
  const overdue = obligations?.filter((o) => o.next_due_date && o.next_due_date < today) ?? [];
  const dueSoon =
    obligations?.filter(
      (o) => o.next_due_date && o.next_due_date >= today && o.next_due_date <= soonIso,
    ) ?? [];

  const score = radar?.postureScore ?? 100;
  const postureLabel = score >= 85 ? "Healthy" : score >= 60 ? "Needs attention" : "At risk";
  const postureTone = score >= 85 ? "clear" : score >= 60 ? "risk" : "breach";

  // Per-fund exposure for the chart
  const byFund =
    radar?.funds.map((f) => {
      const ts = radar.triggers.filter((t) => t.fundId === f.id);
      return {
        name: f.short_name ?? f.name,
        breach: ts.filter((t) => t.severity === "breach").length,
        elevated: ts.filter((t) => t.severity === "elevated").length,
        review: ts.filter((t) => t.severity === "review").length,
      };
    }) ?? [];
  const hasExposure = byFund.some((f) => f.breach + f.elevated + f.review > 0);

  const topFindings = [...(radar?.breaches ?? []), ...(radar?.elevated ?? [])].slice(0, 6);

  return (
    <>
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight text-neutral-50">
            Compliance command center
          </h1>
          <p className="text-sm text-neutral-400">
            Live MFN exposure and obligation posture across {radar?.funds.length ?? "—"} fund
            {radar?.funds.length === 1 ? "" : "s"}, recomputed from the deterministic engine.
          </p>
        </div>
        <Badge tone={postureTone}>
          <Dot tone={postureTone} /> {postureLabel}
        </Badge>
      </header>

      {/* Hero posture band */}
      <Card className="argus-glow overflow-hidden">
        <div className="grid items-center gap-6 p-6 md:grid-cols-[auto_1fr]">
          <div className="flex items-center gap-5">
            <PostureRing score={score} />
          </div>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <HeroStat
              icon={<TriangleAlert className="size-4" />}
              label="Active breaches"
              value={radar?.breaches.length ?? 0}
              tone={(radar?.breaches.length ?? 0) > 0 ? "breach" : "clear"}
              loading={radarLoading}
            />
            <HeroStat
              icon={<Radar className="size-4" />}
              label="Elevated flags"
              value={radar?.elevated.length ?? 0}
              tone={(radar?.elevated.length ?? 0) > 0 ? "risk" : "clear"}
              loading={radarLoading}
            />
            <HeroStat
              icon={<ShieldCheck className="size-4" />}
              label="Commitment exposed"
              value={fmtUsd(radar?.exposedCommitment ?? 0)}
              tone={(radar?.exposedCommitment ?? 0) > 0 ? "risk" : "clear"}
              loading={radarLoading}
            />
            <HeroStat
              icon={<CalendarClock className="size-4" />}
              label="Obligations overdue"
              value={overdue.length}
              tone={overdue.length > 0 ? "breach" : "clear"}
            />
          </div>
        </div>
      </Card>

      {/* Conflict radar + exposure chart */}
      <section className="grid gap-5 lg:grid-cols-[1.4fr_1fr]">
        <Card>
          <CardHeader
            title={
              <span className="flex items-center gap-1.5">
                <Radar className="text-brand-400 size-3.5" /> Conflict radar
              </span>
            }
            action={
              <div className="flex items-center gap-2">
                {latestEvaluation ? (
                  <Badge tone="info">
                    <Dot tone="info" /> Continuous · last evaluated{" "}
                    {relativeTime(latestEvaluation.ran_at)}
                  </Badge>
                ) : null}
                <Link
                  to="/t/$slug/mfn"
                  params={{ slug: tenant.slug }}
                  className="text-[11px] text-neutral-500 transition hover:text-neutral-300"
                >
                  All triggers →
                </Link>
              </div>
            }
          />
          <div className="px-2 pb-2">
            {radarLoading ? (
              <div className="flex items-center gap-2 px-3 py-10 text-sm text-neutral-500">
                <Spinner /> Scanning every side letter for MFN conflicts…
              </div>
            ) : topFindings.length === 0 ? (
              <EmptyState
                icon={<ShieldCheck className="size-7" />}
                title="No active MFN conflicts"
                description="Every LP's most-favored-nation entitlement is satisfied by the terms currently on the book."
              />
            ) : (
              <ul className="divide-y divide-neutral-800/70">
                {topFindings.map((t) => (
                  <li key={`${t.mfnClauseId}-${t.triggeringProvisionId}`} className="px-2 py-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 space-y-1">
                        <div className="flex flex-wrap items-center gap-2 text-sm">
                          <span className="font-medium text-neutral-100">{t.holderName}</span>
                          <ArrowRight className="size-3 text-neutral-600" />
                          <span className="text-neutral-300">{t.beneficiaryName}</span>
                          <span className="text-neutral-600">·</span>
                          <span className="text-xs text-neutral-500">{t.fundName}</span>
                        </div>
                        <p className="line-clamp-2 text-xs leading-relaxed text-neutral-400">
                          {t.rationale}
                        </p>
                      </div>
                      <div className="flex shrink-0 flex-col items-end gap-1.5">
                        <SeverityBadge t={t} />
                        <span className="text-[11px] text-neutral-500">
                          {CATEGORY_LABELS[t.category]}
                        </span>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>

        <Card>
          <CardHeader title="MFN exposure by fund" />
          <div className="px-3 pb-4 pt-1">
            {!hasExposure ? (
              <div className="flex h-[220px] items-center justify-center text-sm text-neutral-600">
                No exposure detected
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={byFund} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 11, fill: "#8a8a98" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fontSize: 11, fill: "#8a8a98" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    cursor={{ fill: "rgba(255,255,255,0.03)" }}
                    contentStyle={{
                      background: "#15151c",
                      border: "1px solid #2a2a35",
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                  />
                  <Bar dataKey="breach" stackId="a" fill="#ef4444" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="elevated" stackId="a" fill="#f59e0b" />
                  <Bar dataKey="review" stackId="a" fill="#3b82f6" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
            <div className="mt-3 flex items-center justify-center gap-4 text-[11px] text-neutral-500">
              <span className="flex items-center gap-1.5">
                <Dot tone="breach" /> Breach
              </span>
              <span className="flex items-center gap-1.5">
                <Dot tone="risk" /> Elevated
              </span>
              <span className="flex items-center gap-1.5">
                <Dot tone="info" /> Review
              </span>
            </div>
          </div>
        </Card>
      </section>

      {/* Obligations + activity */}
      <section className="grid gap-5 lg:grid-cols-2">
        <Card>
          <CardHeader
            title="Upcoming obligations"
            action={
              <Link
                to="/t/$slug/obligations"
                params={{ slug: tenant.slug }}
                className="text-[11px] text-neutral-500 hover:text-neutral-300"
              >
                View all →
              </Link>
            }
          />
          <div className="px-2 pb-2">
            {[...overdue, ...dueSoon].length === 0 ? (
              <p className="px-3 py-6 text-sm text-neutral-500">Nothing due in the next 30 days.</p>
            ) : (
              <ul className="divide-y divide-neutral-800/70">
                {[...overdue, ...dueSoon].slice(0, 6).map((o) => {
                  const rel = relativeDays(o.next_due_date);
                  const overdueItem = rel ? rel.days < 0 : false;
                  return (
                    <li key={o.id} className="flex items-center gap-3 px-2 py-2.5 text-sm">
                      <span
                        className={`w-24 shrink-0 text-xs tabular-nums ${overdueItem ? "text-red-400" : "text-neutral-400"}`}
                      >
                        {fmtDate(o.next_due_date)}
                      </span>
                      <span className="min-w-0 flex-1 truncate text-neutral-200">{o.title}</span>
                      {rel && <Badge tone={overdueItem ? "breach" : "neutral"}>{rel.label}</Badge>}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </Card>

        <Card>
          <CardHeader
            title={
              <span className="flex items-center gap-1.5">
                <Activity className="size-3.5" /> Recent activity
              </span>
            }
            action={
              <Link
                to="/t/$slug/audit"
                params={{ slug: tenant.slug }}
                className="text-[11px] text-neutral-500 hover:text-neutral-300"
              >
                Audit trail →
              </Link>
            }
          />
          <div className="px-2 pb-2">
            {!recentAudit || recentAudit.length === 0 ? (
              <p className="px-3 py-6 text-sm text-neutral-500">No activity yet.</p>
            ) : (
              <ul className="divide-y divide-neutral-800/70">
                {recentAudit.map((e) => (
                  <li key={e.id} className="flex items-center gap-3 px-2 py-2.5 text-xs">
                    <span className="w-28 shrink-0 tabular-nums text-neutral-600">
                      {fmtDateTime(e.created_at)}
                    </span>
                    <span className="font-mono text-[11px] text-neutral-400">{e.action}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>
      </section>
    </>
  );
}

function HeroStat({
  icon,
  label,
  value,
  tone,
  loading,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  tone: "clear" | "risk" | "breach";
  loading?: boolean;
}) {
  const color =
    tone === "breach" ? "text-red-300" : tone === "risk" ? "text-amber-300" : "text-neutral-100";
  return (
    <div className="rounded-lg border border-neutral-800/70 bg-neutral-950/40 p-3.5">
      <div className="flex items-center gap-1.5 text-neutral-500">
        {icon}
        <span className="text-[11px] uppercase tracking-wide">{label}</span>
      </div>
      <div className={`mt-1.5 text-2xl font-semibold tabular-nums tracking-tight ${color}`}>
        {loading ? <span className="text-neutral-600">···</span> : value}
      </div>
    </div>
  );
}

export function TenantDashboardPage() {
  return (
    <TenantLayout>
      <DashboardInner />
    </TenantLayout>
  );
}
